import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

Font.register({
  family: "Manrope",
  fonts: [
    { src: "/fonts/Manrope-Regular.ttf" },
    { src: "/fonts/Manrope-Bold.ttf", fontWeight: "bold" },
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 5,
    paddingBottom: 50,
    paddingHorizontal: 20,
    fontSize: 6,
    fontFamily: "Manrope",
    color: "#333",
  },
  headerSpace: {
    width: "100%",
    height: 80,
    marginBottom: 10,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  header_image: {
    width: "100%",
    height: "100%",
  },
  contentWrapper: {
    paddingHorizontal: 15,
  },
  mainTitle: {
    fontSize: 12,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
    marginBottom: 8,
    paddingBottom: 3,
  },
  tableContainer: {},
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#2663EB",
    borderLeftWidth: 1,
    borderTopWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
  },
  cellHeader: {
    padding: 3,
    fontSize: 5.5,
    color: "#fff",
    fontWeight: "bold",
    textAlign: "center",
    borderRightWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    minHeight: 16,
    alignItems: "center",
  },
  cell: {
    padding: 2,
    fontSize: 5.5,
    textAlign: "center",
    borderRightWidth: 1,
    borderColor: "#000",
    borderStyle: "solid",
    minHeight: 16,
  },
  lastCell: {
    padding: 2,
    fontSize: 5.5,
    textAlign: "center",
    borderRightWidth: 0,
    borderColor: "#000",
    borderStyle: "solid",
    minHeight: 16,
  },
  footer: {
    position: "absolute",
    bottom: 15,
    left: 30,
    right: 30,
    textAlign: "center",
    fontSize: 7,
    color: "grey",
  },
});

const colWidths = [
  "3.5%",  // 0  Emp ID
  "11%",   // 1  Name
  "5.5%",  // 2  Title
  "6.5%",  // 3  Department
  "4.5%",  // 4  Division
  "4.5%",  // 5  Status
  "5.5%",  // 6  Join Date
  "5%",    // 7  Basic Pay
  "5%",    // 8  Allowances
  "4%",    // 9  Increments
  "3%",    // 10 Nr Hrs
  "3%",    // 11 Ot Hrs
  "3%",    // 12 Ots Hrs
  "3%",    // 13 Abs Days
  "5%",    // 14 Total Wage
  "4.5%",  // 15 Additions
  "5.5%",  // 16 Gross Pay
  "5%",    // 17 Deductions
  "5.5%",  // 18 Net Pay
  "7.5%",  // 19 Loans Outstanding
];

const formatCurrency = (amt) =>
  Number(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });

const PayrollListDocument = ({ payrollListPrintData }) => {
  console.log("payrollListPrintData:", payrollListPrintData)
  const bondTimeportNoData = JSON.parse(
    localStorage.getItem("BondTimeportNoData") || "[]",
  );
  const headerImage = bondTimeportNoData[0]?.REP_HEADER;
  const footerImage = bondTimeportNoData[0]?.REP_FOOTER;

  const headers = [
    "Emp ID", "Name", "Title", "Department", "Division", "Status",
    "Join Date","Basic Pay", "Allowances", "Increments", "Nr Hrs", "Ot Hrs", "Ots Hrs",
    "Abs Days", "Total Wage", "Additions","Gross Pay","Deductions", "Net Pay" , "Loans Outstanding",
  ];

  return (
    <Document>
      <Page size="A4" orientation="landscape" style={styles.page}>
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>
        <View style={styles.contentWrapper}>
          <Text style={styles.mainTitle}>Payroll List</Text>

          <View style={styles.tableContainer}>
            {/* Header Row */}
            <View style={styles.tableHeader}>
              {headers.map((h, i) => {
                const isLast = i === headers.length - 1;
                return (
                  <Text
                    key={i}
                    style={[
                      styles.cellHeader,
                      { width: colWidths[i] },
                      isLast && { borderRightWidth: 0 }
                    ]}
                  >
                    {h}
                  </Text>
                );
              })}
            </View>

            {/* Data Rows */}
            {payrollListPrintData?.map((d, i) => (
              <View key={i} style={styles.tableRow}>
                <Text style={[styles.cell, { width: colWidths[0] }]}>
                  {d.EMP_ID}
                </Text>
                <Text style={[styles.cell, { width: colWidths[1] }]}>
                  {d.NAME}
                </Text>
                <Text style={[styles.cell, { width: colWidths[2] }]}>
                  {d.JOB_TITLE}
                </Text>
                <Text style={[styles.cell, { width: colWidths[3] }]}>
                  {d.DEPARTMENT}
                </Text>
                <Text style={[styles.cell, { width: colWidths[4] }]}>
                  {d.BRANCH}
                </Text>
                <Text style={[styles.cell, { width: colWidths[5] }]}>
                  {d.STATUS}
                </Text>
                <Text style={[styles.cell, { width: colWidths[6] }]}>
                  {d.JOIN_DATE ? dayjs(d.JOIN_DATE).format("DD-MMM-YYYY") : "-"}
                </Text>
                <Text style={[styles.cell, { width: colWidths[7] }]}>
                  {formatCurrency(d.BASIC_PAY)}
                </Text>
                <Text style={[styles.cell, { width: colWidths[8] }]}>
                  {formatCurrency(d.ALLOWANCES)}
                </Text>
                <Text style={[styles.cell, { width: colWidths[9] }]}>
                  {formatCurrency(d.INCREMENTS)}
                </Text>
                <Text style={[styles.cell, { width: colWidths[10] }]}>
                  {d.NR_HRS}
                </Text>
                <Text style={[styles.cell, { width: colWidths[11] }]}>
                  {d.OT_HRS}
                </Text>
                <Text style={[styles.cell, { width: colWidths[12] }]}>
                  {d.OTS_HRS}
                </Text>
                <Text style={[styles.cell, { width: colWidths[13] }]}>
                  {d.ABS_DAYS}
                </Text>
                <Text style={[styles.cell, { width: colWidths[14] }]}>
                  {formatCurrency(d.TOTAL_WAGE)}
                </Text>
                <Text style={[styles.cell, { width: colWidths[15] }]}>
                  {formatCurrency(d.OTH_ADDITIONS) || "-"}
                </Text>
                <Text style={[styles.cell, { width: colWidths[16] }]}>
                  {formatCurrency(d.GROSS_PAY) || "-"}
                </Text>
                <Text style={[styles.cell, { width: colWidths[17] }]}>
                  {formatCurrency(d.DEDUCTIONS) || "-"}
                </Text>
                <Text style={[styles.cell, { width: colWidths[18] }]}>
                  {formatCurrency(d.NET_PAY) || "-"}
                </Text>
                <Text style={[styles.lastCell, { width: colWidths[19] }]}>
                  {formatCurrency(d.LOANS_AND_ADV) || "-"}
                </Text>
              </View>
            ))}
          </View>
        </View>
        <View style={styles.footer} fixed>
          {footerImage && (
            <Image src={footerImage} style={styles.headerSpace} />
          )}
        </View>
      </Page>
    </Document>
  );
};

export default PayrollListDocument;